import { useState, useEffect } from "react";
import { commentsAPI } from "../api/index.js";
import { useAuth } from "../hooks/useAuth.jsx";
import SignInPrompt from "./SignInPrompt.jsx";

const G = {
  surface: "#111111", border: "#1E1E1E", text: "#E4E4DC",
  muted: "#666", accent: "#00C896", danger: "#FF6B6B",
  font: "'Sora', sans-serif", mono: "'DM Mono', monospace",
};

export default function Comments({ answerId }) {
  const { user } = useAuth();
  const [comments, setComments]   = useState([]);
  const [showAll, setShowAll]     = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [body, setBody]           = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState("");

  const load = async () => {
    try {
      const res = await commentsAPI.list(answerId);
      setComments(res.data.comments);
    } catch {}
  };

  useEffect(() => { load(); }, [answerId]);

  const handleSubmit = async () => {
    if (body.trim().length < 2) return setError("Comment too short");
    setSubmitting(true); setError("");
    try {
      await commentsAPI.create({ body, answer_id: answerId });
      setBody("");
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.response?.data?.errors?.[0]?.msg || "Failed to post comment");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await commentsAPI.delete(id); load(); } catch {}
  };

  const visible = showAll ? comments : comments.slice(0, 3);

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid #1A1A1A` }}>

      {/* Comment list */}
      {visible.map(c => (
        <div key={c.id} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
          <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#1A1A1A", border: `1px solid #2A2A2A`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, color: G.muted, fontFamily: G.mono, flexShrink: 0, marginTop: 2 }}>
            {c.author_username?.slice(0,2).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 12, color: G.muted, fontFamily: G.mono, marginRight: 6 }}>{c.author_username}</span>
            <span style={{ fontSize: 13, color: "#AAA", lineHeight: 1.5 }}>{c.body}</span>
            <span style={{ fontSize: 10, color: "#333", fontFamily: G.mono, marginLeft: 8 }}>{new Date(c.created_at).toLocaleDateString()}</span>
            {user?.id === c.author_id && (
              <button onClick={() => handleDelete(c.id)} style={{ background: "none", border: "none", color: "#333", cursor: "pointer", fontSize: 10, marginLeft: 8, fontFamily: G.mono }}
                onMouseOver={e => e.target.style.color = G.danger}
                onMouseOut={e => e.target.style.color = "#333"}>
                delete
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Show more */}
      {comments.length > 3 && !showAll && (
        <button onClick={() => setShowAll(true)} style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 12, fontFamily: G.mono, marginBottom: 8 }}>
          + {comments.length - 3} more comment{comments.length - 3 !== 1 ? "s" : ""}
        </button>
      )}

      {/* Add comment */}
      {user ? (
        showForm ? (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <input value={body} onChange={e => setBody(e.target.value)}
                placeholder="Add a comment..."
                onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }}
                style={{ flex: 1, background: "#0D0D0D", border: `1px solid #2A2A2A`, color: G.text, borderRadius: 6, padding: "7px 12px", fontSize: 13, fontFamily: G.font, outline: "none" }}
                onFocus={e => e.target.style.borderColor = G.accent}
                onBlur={e => e.target.style.borderColor = "#2A2A2A"}
                autoFocus />
              <button onClick={handleSubmit} disabled={submitting || body.trim().length < 2}
                style={{ background: G.accent, border: "none", color: "#000", padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontFamily: G.mono, fontWeight: 600, opacity: submitting || body.trim().length < 2 ? 0.4 : 1 }}>
                Post
              </button>
              <button onClick={() => { setShowForm(false); setBody(""); setError(""); }}
                style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 12, fontFamily: G.mono }}>
                Cancel
              </button>
            </div>
            {error && <div style={{ fontSize: 12, color: G.danger, marginTop: 4, fontFamily: G.mono }}>{error}</div>}
          </div>
        ) : (
          <button onClick={() => setShowForm(true)}
            style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 12, fontFamily: G.mono, marginTop: 4, transition: "color 0.15s" }}
            onMouseOver={e => e.target.style.color = G.accent}
            onMouseOut={e => e.target.style.color = "#444"}>
            + Add comment
          </button>
        )
      ) : (
        <button onClick={() => setShowPrompt(true)}
          style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 12, fontFamily: G.mono, marginTop: 4, transition: "color 0.15s" }}
          onMouseOver={e => e.target.style.color = G.accent}
          onMouseOut={e => e.target.style.color = "#444"}>
          + Add comment
        </button>
      )}
      {showPrompt && <SignInPrompt action="comment" onClose={() => setShowPrompt(false)} />}
    </div>
  );
}
